import { useContext, useEffect, useState, useRef } from "react";
import { Box } from "@mui/material";
import TkStepper from "../../components/atoms/TkStepper";
import { contextText, fakeTableList, schema, steps } from "./parts";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import { GlobalContext } from "../../context/globalContext";
import {
  ConfirmStatus,
  formClassCorp,
  FormKbn,
  NoOwnerReason,
} from "../../assets/data/Constants";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { BusinessContext } from "../../context/businessContext";
import {
  httpAPI_CON006,
  httpAPI_CON013,
  httpAPI_CON030,
  httpAPI_CON031,
} from "./api/request";
import { useLocation, useNavigate } from "react-router-dom";
import { IAPI_CON006_Response, IAPI_CON013_Request } from "../../type/apiType";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import TkInfoManual from "../../components/atoms/TkInfoManual";
import _ from "lodash";
import TkPropertyMessageGet from "../../utils/TkPropertyMessageGet";

/**
 * SCRC050106 契約解除（経営者でなくなった）内容入力
 * 契約解除（契約者でなくなった）の内容を入力する。
 * @return SCRC050106
 */
const SCRC050106 = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);

  const [stateTableList, setStateTableList] = useState<any>([]);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));
  const [selectedOther, setSelectedOther] = useState(false);
  const [resData, setResData] = useState<IAPI_CON006_Response>();
  const [open, setOpen] = useState(false);

  const userType = state.authInfo?.userType;
  const isKiko = userType === authorityUserType.userTypeNumber.KIKO;
  const contractorNo = isKiko
    ? businessState.common?.contractorNo
    : state.authInfo?.contractorNo!;
  const applyId = location.state?.applyId ?? businessState.job_02?.applyId;

  const [openData, setOpenData] = useState<any>();
  const inputDataUseRef = useRef<any>();
  const [openWarning, setOpenWarning] = useState(false);
  const [isSave, setIsSave] = useState(false);

  const handleInit = async () => {
    try {
      // 社会福祉施設等及び特定介護保険施設等の経営者でなくなった者の届参照API実行
      const res = await httpAPI_CON006({
        applyId: applyId,
        contractorNo: contractorNo,
      });

      if (res.resultCd === "I0000") {
        let resArr = stateFakeTableList.current;

        for (let i in res) {
          let targetObj = resArr.find((item) => item.keyword === i)!;
          targetObj && (targetObj.setDefaultValue = res[i as keyof typeof res]);
        }

        let noOwnerReason = resArr.find(
          (item) => item.keyword === "noOwnerReason"
        )!;
        noOwnerReason.onGetValue = (e: any) => {
          setSelectedOther(e === NoOwnerReason["その他"]);
        };
        if (res.noOwnerReason === NoOwnerReason["その他"]) {
          setSelectedOther(true);
        } else {
          setSelectedOther(false);
          let reason = resArr.find((item) => item.keyword === "reason")!;
          reason.setDefaultValue = "";
        }

        // 廃止・休止が確認できる書類の添付
        let attachedFile = resArr.find(
          (item) => item.keyword === "attachedFile"
        )!;
        const targetFile = res.formList?.find(
          (item: any) =>
            item.formClass === formClassCorp["廃止・休止が確認できる書類の添付"]
        );
        targetFile &&
          (attachedFile.setDefaultValue = [
            {
              formName: targetFile.fileName,
              fileBase64: targetFile.fileBase64,
            },
          ]);

        setResData(res);
        setStateTableList([...resArr]);
        setState({ ...state, errorMsgId: "" });
      } else {
        setState({ ...state, errorMsgId: res.resultCd });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  // 初期表示
  useEffect(() => {
    let resArr = stateFakeTableList.current;

    let noOwnerReason = resArr.find(
      (item) => item.keyword === "noOwnerReason"
    )!;
    noOwnerReason.onGetValue = (e: any) => {
      setSelectedOther(e === NoOwnerReason["その他"]);
    };

    let contractorName = resArr.find(
      (item) => item.keyword === "contractorName"
    )!;
    contractorName.contractorNumber =
      businessState.common?.contractorNo +
      ":" +
      businessState.common?.contractorName;
    contractorName.showLine = isKiko;

    setStateTableList([...resArr]);

    // 持回情報.申請idがある場合
    if (!!applyId || !!contractorNo) {
      handleInit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let reason = resArr.find((item) => item.keyword === "reason")!;

    if (!selectedOther) {
      reason.setDefaultValue = "";
    }
    reason.disabled = !selectedOther;

    setStateTableList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOther]);

  const makeHttpObj = (obj: any): IAPI_CON013_Request => {
    const filteredFormList =
      resData?.formList?.filter(
        (itme) =>
          itme.formClass !== formClassCorp["廃止・休止が確認できる書類の添付"]
      ) ?? [];
    const formList: any = [
      ...filteredFormList,
      {
        serialNo: filteredFormList.length + 1,
        facilityNo: "",
        staffNo: "",
        formKbn: FormKbn["法人書類"],
        formClass: formClassCorp["廃止・休止が確認できる書類の添付"],
        fileName: obj.attachedFile[0].formName,
        fileBase64: obj.attachedFile[0].fileBase64,
      },
    ];
    const reqObj: IAPI_CON013_Request = {
      applyId: resData?.applyId ?? applyId,
      tempSaveStep: 1,
      contractorNo,
      noOwnerDate: obj.noOwnerDate,
      noOwnerReason: obj.noOwnerReason,
      reason: obj.reason,
      formList,
      aContractorNo: obj.aContractorNo,
      aCorpName: obj.aCorpName,
      facilityList: [],
      decisionDate: "",
      symbolNo: "",
      receptionDate: "",
      screenId: "SCRC050106",
    };

    return reqObj;
  };

  // 業務チェック
  const businessCheck = (noOwnerDate: any): string => {
    const inputDate = new Date(noOwnerDate);
    const confirmStatus = state.authInfo?.confirmStatus;
    const fiscalYear = state.authInfo?.fiscalYear!;
    if (
      !(
        confirmStatus === ConfirmStatus["確定済"] ||
        confirmStatus === ConfirmStatus["過去年度"]
      )
    ) {
      // 当年度の確定前、かつ　入出力項目.経営者でなくなった年月日 ≦ 前年３月３１日に変更
      if (inputDate <= new Date(fiscalYear - 1 + "/3/31")) {
        return "TKMSG_SCR0119E";
      }
    }
    return "";
  };

  const handleSubmit = async (data: any, index: any) => {
    inputDataUseRef.current = data;
    const isSave = index === 0;
    setIsSave(isSave);

    // 相関チェック
    const errorMsgId = businessCheck(data.noOwnerDate);
    setState({
      ...state,
      errorMsgId,
    });
    if (errorMsgId) return;

    const today = new Date();
    const inputDate = new Date(data.noOwnerDate).setHours(0, 0, 0, 0);
    const nextYearDate = new Date(
      today.setFullYear(today.getFullYear() + 1)
    ).setHours(0, 0, 0, 0);
    if (inputDate >= nextYearDate) {
      //入出力項目.経営者でなくなった年月日 ≧ システム日付 + 1年
      setOpenWarning(true);
    } else {
      handleTransferCheck(data, isSave);
    }
  };

  const handleTransferCheck = async (data: any, temp: boolean) => {
    // 日々更新法人職員系の退職年月日削除職員確認API 呼び出し
    try {
      const res = await httpAPI_CON031({
        contractorNo,
        businessType: businessState.common?.businessType,
        cancelDate: data.noOwnerDate,
      });

      if (res.resultCd === "I0000") {
        if (res.staffNoInfoList?.length) {
          setOpenData(res.staffNoInfoList);
        } else {
          handleUpdate(data, temp);
        }
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
        return;
      }
    } catch {
      navigate("/error");
    }
  };

  const handleUpdate = async (data: any, temp: boolean) => {
    try {
      // 社会福祉施設等及び特定介護保険施設等の経営者でなくなった者の届更新API実行
      const res = await httpAPI_CON013({
        ...makeHttpObj(data),
      });

      if (res.resultCd === "I0000") {
        setBusinessState({
          ...businessState,
          job_02: {
            ...businessState.job_02,
            applyId: res?.applyId,
            noOwnerDate: data.noOwnerDate,
            noOwnerReason: data.noOwnerReason,
            reason: data.reason,
            fileName: data.attachedFile[0].formName,
            fileBase64: data.attachedFile[0].fileBase64,
            aContractorNo: data.aContractorNo,
            aCorpName: data.aCorpName,
            tempSaveStep: 1,
          },
        });

        if (temp) {
          setOpen(true);
        } else {
          navigate("/SCRC050107");
        }
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch {
      navigate("/error");
    }
  };

  // 異動情報削除確認ダイアログ
  const dialogOk = async () => {
    setOpenData(undefined);

    // 日々更新法人職員系の退職年月日削除API 呼び出し
    try {
      const res = await httpAPI_CON030({
        contractorNo,
        staffNoInfoList: openData,
        businessType: businessState.common?.businessType,
        applyId: applyId,
      });

      if (res.resultCd === "I0000") {
        handleUpdate(inputDataUseRef.current, isSave);
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const onClickWarningOk = () => {
    setOpenWarning(false);
    handleTransferCheck(inputDataUseRef.current, isSave);
  };

  return (
    <Box data-testid="SCRC050106PageForTest" className="SCRC050106">
      <TkStepper steps={steps} activeStep={0} />

      <TkFakeTable
        list={stateTableList}
        ruleSchema={schema}
        buttonGroup={<TkBackButton />}
        submitButtonGroup={[
          { submitButtonText: "一時保存" },
          { submitButtonText: "次へ" },
        ]}
        onCheckAndSubmit={handleSubmit}
      />

      <TkMessageDialog
        open={open}
        onOky={() => setOpen(false)}
        title="一時保存完了"
        contextText="　一時保存が完了しました。"
        okText="はい"
        cancelText=""
      />

      <TkMessageDialog
        open={!!openData}
        onOky={dialogOk}
        onCancel={() => setOpenData(undefined)}
        title="確認（退職(予定)年月日・異動情報(配置換え)が削除されます）"
        children={<TkInfoManual rule={"<"}>{contextText}</TkInfoManual>}
        cancelText="いいえ"
        okText="はい"
      />

      <TkMessageDialog
        open={openWarning}
        onOky={onClickWarningOk}
        onCancel={() => setOpenWarning(false)}
        title="警告"
        contextText={TkPropertyMessageGet("TKMSG_SCR0034W")}
        okText="確認"
        cancelText="戻る"
      />
    </Box>
  );
};

export default SCRC050106;
